import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';

import { meKeys } from './meKeys';

// Wire shape for GET /api/v1/me. Mirrors backend's UserProfileDto.
// Defined here (not in features/auth/schemas) because this is the
// settings-feature's view of "me" — broader than the JWT-derived
// SessionUser, includes editable profile fields and joined role name.
export const userProfileSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  fullName: z.string().nullable(),
  tenantId: z.string().nullable(),
  roleName: z.string().nullable(),
  isSuperAdmin: z.boolean(),
  onboardingCompleted: z.boolean(),
  isEmailVerified: z.boolean(),
});

export type UserProfile = z.infer<typeof userProfileSchema>;

export function useGetMe() {
  return useQuery({
    queryKey: meKeys.detail(),
    queryFn: async ({ signal }) => {
      const raw = await apiClient.get('api/v1/me', { signal }).json();
      // Validate at the wire boundary -- the type system can lie but Zod
      // can't, and a missing `firstName` shape change in the backend
      // would otherwise surface as a confusing render bug far from the
      // root cause.
      return userProfileSchema.parse(raw);
    },
    // Profile changes are rare; keep it cached for 60s before considering
    // it stale. Invalidation after PATCH /me forces an immediate refetch
    // regardless of stale-time.
    staleTime: 60_000,
  });
}
